<template>
  <b-nav-item-dropdown
      v-if="canSeeReminder && incompleteBranches.length > 0"
      ref="dd"
      right
      :class="{ 'sr-dark': isDark }"
      toggle-class="setup-reminder-toggle"
      menu-class="setup-reminder-menu"
  >
    <template #button-content>
      <span class="setup-reminder-pill" :class="{ 'sr-dark': isDark }" v-b-tooltip.hover :title="msg('Branches that still need setup')">
        <feather-icon icon="AlertTriangleIcon" size="15"/>
        <span class="setup-reminder-pill__text d-none d-md-inline">{{ msg('Setup') }}</span>
        <span class="setup-reminder-pill__badge">{{ incompleteBranches.length }}</span>
      </span>
    </template>

    <div class="setup-reminder-header">
      <feather-icon icon="ClipboardIcon" size="15" class="mr-50"/>
      {{ msg('Finish branch setup') }}
    </div>

    <b-dropdown-item
        v-for="b in incompleteBranches"
        :key="b.id"
        @click="goToWizard(b.id)"
    >
      <div class="setup-reminder-row">
        <div class="setup-reminder-ring" :class="{ 'sr-dark': isDark }" :style="ringStyle(b.percentage)">
          <span>{{ b.percentage }}%</span>
        </div>
        <div class="setup-reminder-info">
          <div class="setup-reminder-name">{{ b.name }}</div>
          <div class="setup-reminder-sub">{{ msg('Tap to continue setup') }}</div>
        </div>
        <feather-icon icon="ChevronRightIcon" size="16" class="setup-reminder-chev"/>
      </div>
    </b-dropdown-item>
  </b-nav-item-dropdown>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import useAppConfig from '@core/app-config/useAppConfig'
import { computed } from '@vue/composition-api'

export default {
  name: 'SetupReminderDropdown',
  setup() {
    const { skin } = useAppConfig()
    // The navbar renders dark in BOTH 'dark' and 'semi-dark' skins, so treat
    // both as dark for the pill's contrast.
    const isDark = computed(() => skin.value === 'dark' || skin.value === 'semi-dark')
    return { isDark }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('setupWizard', ['getCompletionByBranch']),
    canSeeReminder() {
      // Only users who can edit branches can act on the wizard.
      return this.$store.getters['user/canEdit']('businessunit')
          || this.$store.getters['user/canRead']('businessunit');
    },
    allBranches() {
      return this.$store.getters['data/getAllBranches'] || {};
    },
    incompleteBranches() {
      const map = this.getCompletionByBranch || {};
      const branches = this.allBranches;
      return Object.keys(map)
          .map(id => {
            const pct = Number(map[id]);
            const branch = branches[id] || branches[Number(id)] || {};
            return {
              id: id,
              percentage: isNaN(pct) ? 0 : pct,
              name: branch.name || (this.msg('Branch') + ' #' + id),
            };
          })
          .filter(b => b.percentage < 100)
          .sort((a, b) => a.percentage - b.percentage);
    },
  },
  methods: {
    ...mapActions('setupWizard', ['fetchAllSetupStatus']),
    refresh() {
      if (this.canSeeReminder) this.fetchAllSetupStatus();
    },
    goToWizard(branchId) {
      if (this.$refs.dd) this.$refs.dd.hide(true);
      const target = `/organization/branches/setup/${branchId}`;
      if (this.$route.fullPath !== target) this.$router.push(target);
    },
    ringStyle(pct) {
      const deg = Math.round((pct / 100) * 360);
      return {
        background: `conic-gradient(#D4A96A ${deg}deg, rgba(212,169,106,0.18) ${deg}deg)`,
      };
    },
  },
  mounted() {
    this.refresh();
    // Re-check when branches change (e.g. branch switcher) or after edits.
    this.$root.$on('crm::branch-updated', this.refresh);
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.refresh);
  },
}
</script>

<style lang="scss" scoped>
.setup-reminder-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #B8894C;
  background: rgba(212, 169, 106, 0.12);
  border: 1px solid rgba(212, 169, 106, 0.30);
  transition: background 0.18s ease;
}
.setup-reminder-pill:hover { background: rgba(212, 169, 106, 0.20); }

/* Dark layout: brighter gold for contrast on the navy navbar.
   Both the JS-driven .sr-dark class AND the body .dark-layout class are
   covered so it works regardless of how dark mode was triggered. The icon
   stroke is forced explicitly because feather SVGs don't always inherit
   the parent's currentColor reliably inside the nav-link toggle. */
.setup-reminder-pill.sr-dark,
.dark-layout .setup-reminder-pill {
  color: #EDCFA0 !important;
  background: rgba(237, 207, 160, 0.14);
  border-color: rgba(237, 207, 160, 0.32);
}
.setup-reminder-pill.sr-dark:hover,
.dark-layout .setup-reminder-pill:hover { background: rgba(237, 207, 160, 0.22); }

.setup-reminder-pill.sr-dark svg,
.dark-layout .setup-reminder-pill svg { stroke: #EDCFA0 !important; }
.setup-reminder-pill__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #D4A96A;
  color: #1a1205;
  font-size: 11px;
  font-weight: 800;
}

.setup-reminder-header {
  padding: 10px 16px 8px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #B8894C;
  display: flex;
  align-items: center;
}

.setup-reminder-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
  min-width: 260px;
}
.setup-reminder-ring {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9.5px;
  font-weight: 800;
  color: #B8894C;
}
.setup-reminder-ring span {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: #fff;
  display: flex; align-items: center; justify-content: center;
}
/* Dark: ring center matches the dark dropdown surface + brighter gold text */
.setup-reminder-ring.sr-dark { color: #EDCFA0; }
.setup-reminder-ring.sr-dark span { background: #283046; }

.setup-reminder-info { flex: 1; min-width: 0; }
.setup-reminder-name {
  font-size: 13.5px; font-weight: 700;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.setup-reminder-sub { font-size: 11px; opacity: 0.6; }
.setup-reminder-chev { opacity: 0.5; flex-shrink: 0; }
</style>
